import * as React from 'react';
import { useEffect } from 'react';
import { CookieType } from 'components/Gdpr/enums/CookieType';
import { ICustomerConsentResponse } from 'components/Gdpr/requests/consentRequest';
import { useGdprConsent } from 'hooks/useGdprConsent';
import { useAuth } from 'hooks/useAuth';
import { ConsentState, IDataLayerItem } from 'components/SeoSuite/interfaces/IDataLayer';
import {
    CartAlteredAction,
    CheckoutStepAction,
    ICartAlteredDetail,
    ICheckoutStepDetail,
    ISearchDetail,
    ISelectPromotionContext,
    IViewItemListDetail,
} from 'components/SeoSuite/interfaces/IGtmEventDetail';
import {
    GTM_EVENT,
    getCurrencyCode,
    getItemsValue,
    pushDataLayer,
    setUserId,
    toDataLayerItem,
    updateConsent,
} from 'components/SeoSuite/helpers/dataLayer';

const PROMOTIONAL_SLIDE_SELECTOR = '[data-promotional-slide]';

/** Google consent mode signals granted by each of our cookie categories. */
const CONSENT_SIGNALS: Record<string, string[]> = {
    [CookieType.CODE_NECCESSARY]: ['security_storage'],
    [CookieType.CODE_ANALYTICS]: ['analytics_storage'],
    [CookieType.CODE_MARKETING]: ['ad_storage', 'ad_user_data', 'ad_personalization'],
    [CookieType.CODE_PREFERENCE]: ['functionality_storage', 'personalization_storage'],
};

const CART_EVENT_NAMES: Record<CartAlteredAction, string> = {
    'add-to-cart': 'add_to_cart',
    'remove-from-cart': 'remove_from_cart',
};

const CHECKOUT_EVENT_NAMES: Record<CheckoutStepAction, string> = {
    'add-contact-info': 'add_contact_info',
    'add-shipping-info': 'add_shipping_info',
    'add-payment-info': 'add_payment_info',
};

/** Page load events can fire before consent and auth resolve, so they are replayed afterwards. */
const queueableEvents = [GTM_EVENT.viewItemList, GTM_EVENT.search];
const eventQueue: CustomEvent[] = [];

const enqueueEvent = (e: Event) => {
    eventQueue.push(e as CustomEvent);
};

const replayQueuedEvents = () => {
    eventQueue.splice(0).forEach((e) => window.dispatchEvent(new CustomEvent(e.type, { detail: e.detail })));
};

const pushConsentState = (customerConsent?: ICustomerConsentResponse) => {
    const consent: Record<string, ConsentState> = {};

    (customerConsent?.data ?? []).forEach((cookie) => {
        const state: ConsentState = cookie.status ? 'granted' : 'denied';

        (CONSENT_SIGNALS[cookie.code] ?? []).forEach((signal) => {
            consent[signal] = state;
        });
    });

    if (Object.keys(consent).length) {
        updateConsent(consent);
    }
};

/** Data layers Magento rendered for the page that is currently open (view_item, view_cart, purchase, ...). */
const pushServerDataLayers = () => {
    (window.serverDataLayers ?? []).forEach((dataLayer) => pushDataLayer(dataLayer));

    window.serverDataLayers = [];
};

const handleViewItemList = (e: Event) => {
    const details = (e as CustomEvent<IViewItemListDetail>).detail;

    if (!details?.items?.length) {
        return;
    }

    pushDataLayer({
        event: 'view_item_list',
        ecommerce: {
            currency: getCurrencyCode(),
            item_list_id: details.list.id,
            item_list_name: details.list.name,
            items: details.items.map(toDataLayerItem),
        },
    });
};

const handleCartAltered = (e: Event) => {
    const details = (e as CustomEvent<ICartAlteredDetail>).detail;

    // cart-altered doubles as a plain "refresh the cart" signal, those carry no products.
    if (!details?.action || !details.items?.length) {
        return;
    }

    const items = details.items.map(toDataLayerItem);

    pushDataLayer({
        event: CART_EVENT_NAMES[details.action],
        ecommerce: {
            currency: getCurrencyCode(),
            value: getItemsValue(items),
            items,
        },
    });
};

const getPaymentType = (details: ICheckoutStepDetail): string => {
    const { methodCode, methodName } = details.method ?? {};

    return methodName ? `${methodCode}-${methodName}` : methodCode ?? '';
};

const handleCheckoutStep = (e: Event) => {
    const details = (e as CustomEvent<ICheckoutStepDetail>).detail;

    if (!details?.action || !CHECKOUT_EVENT_NAMES[details.action]) {
        return;
    }

    const items: IDataLayerItem[] = (details.items ?? []).map((item) =>
        toDataLayerItem({
            sku: item.sku ?? '',
            name: item.name,
            price: item.price,
            quantity: item.qty || 1,
        }),
    );

    pushDataLayer({
        event: CHECKOUT_EVENT_NAMES[details.action],
        ecommerce: {
            currency: getCurrencyCode(),
            value: getItemsValue(items),
            items,
            ...(details.action === 'add-shipping-info' && { shipping_tier: details.method?.methodCode }),
            ...(details.action === 'add-payment-info' && { payment_type: getPaymentType(details) }),
        },
    });
};

const handleSearch = (e: Event) => {
    const details = (e as CustomEvent<ISearchDetail>).detail;

    if (!details?.term) {
        return;
    }

    pushDataLayer({
        event: 'search',
        ecommerce: {
            search_term: details.term,
            search_type: details.type,
            search_results_count: details.resultsCount,
        },
    });
};

/**
 * Campaign banners are rendered by Magento, so the promotion payload travels on the markup itself.
 */
const handlePromotionClick = (e: MouseEvent) => {
    const link = (e.target as HTMLElement | null)?.closest?.('a');
    const slide = link?.closest(PROMOTIONAL_SLIDE_SELECTOR);

    if (!slide) {
        return;
    }

    let context: ISelectPromotionContext;

    try {
        context = JSON.parse(slide.getAttribute('data-gtm-context') ?? '{}');
    } catch (error) {
        return;
    }

    pushDataLayer({
        event: 'select_promotion',
        ecommerce: {
            promotion_name: context.promotionName ?? context.creativeName,
            creative_name: context.creativeName,
            creative_slot: context.creativeSlot,
        },
    });
};

export const GtagEvents = (): JSX.Element => {
    const { isFinished: consentResolved, customerConsent, hasConsent } = useGdprConsent();
    const { isFinished: authResolved, customer } = useAuth();
    const isReady = consentResolved && authResolved;

    useEffect(() => {
        if (!isReady) {
            queueableEvents.forEach((name) => window.addEventListener(name, enqueueEvent));

            return () => queueableEvents.forEach((name) => window.removeEventListener(name, enqueueEvent));
        }

        pushConsentState(customerConsent);

        if (!hasConsent(CookieType.CODE_ANALYTICS)) {
            return;
        }

        setUserId(customer?.id ? String(customer.id) : null);

        window.addEventListener(GTM_EVENT.viewItemList, handleViewItemList);
        window.addEventListener(GTM_EVENT.cartAltered, handleCartAltered);
        window.addEventListener(GTM_EVENT.checkoutStepProceed, handleCheckoutStep);
        window.addEventListener(GTM_EVENT.search, handleSearch);
        document.addEventListener('click', handlePromotionClick);

        pushServerDataLayers();
        replayQueuedEvents();

        return () => {
            window.removeEventListener(GTM_EVENT.viewItemList, handleViewItemList);
            window.removeEventListener(GTM_EVENT.cartAltered, handleCartAltered);
            window.removeEventListener(GTM_EVENT.checkoutStepProceed, handleCheckoutStep);
            window.removeEventListener(GTM_EVENT.search, handleSearch);
            document.removeEventListener('click', handlePromotionClick);
        };
    }, [isReady, customerConsent, customer]);

    return <React.Fragment />;
};
